import { useEffect, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { FilterSelections } from '@modernanimal/common/src/web/SmartBookingFlow/smartBookingFlow.helpers';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

import { GET_AVAILABLE_SLOTS, GET_REGION_LOCATIONS } from './appointmentSelection.v3.gql';
import { Slot } from './index';
import { LocationPartsFragment } from '../__generated__/graphql';

const VIRTUAL_CARE_LOCATION_ID = '1';

// Simplified version of useLocationCreditCardSetting
export const useLocationCreditCardSetting = () => {
    const [searchParams] = useSearchParams();
    const locationId = searchParams.get('location');
    const [showCreditCardStep, setShowCreditCardStep] = useState<boolean>(false);
    const [hasFetchedLocation, setHasFetchedLocation] = useState(false);

    const { loading } = useQuery(GET_REGION_LOCATIONS, {
        variables: { id: locationId || '0' },
        skip: !locationId || hasFetchedLocation,
        onCompleted: (data) => {
            if (data?.locations?.[0]?.region?.locationSet) {
                const locations = data.locations[0].region.locationSet.filter(
                    (location: LocationPartsFragment) => location?.id !== VIRTUAL_CARE_LOCATION_ID
                );

                // Find the location that matches our URL parameter
                const selectedLocation = locations.find(
                    (location: LocationPartsFragment) => location.id === locationId
                );

                setShowCreditCardStep(!!selectedLocation?.webBookingShowCreditCardStep);
            }
            setHasFetchedLocation(true);
        },
        onError: (error) => {
            console.error('Error loading location credit card setting:', error.message);
            Sentry.captureException(error, {
                extra: {
                    locationId,
                    path: 'src/AppointmentSelection/hooks',
                    function: 'useLocationCreditCardSetting'
                },
            });
            setHasFetchedLocation(true);
        }
    });

    return { showCreditCardStep, isLoadingCreditCardToggle: loading };
};

type LoadRegionLocationsOnMountHookParams = {
    appointmentFilters: FilterSelections,
    setInitialRegionLocations: (
        locations: LocationPartsFragment[],
        selectedLocation: LocationPartsFragment | undefined,
        regionId: string,
    )=> void,
    setLocations: (locations: LocationPartsFragment[])=> void,
};

// This hook assumes that the only way to change the regionId is by loading the page with a location
// in a different region, and not by changing the regionId within another page component.
export const useLoadRegionLocationsOnMount = (params: LoadRegionLocationsOnMountHookParams) => {
    const {
        appointmentFilters,
        setInitialRegionLocations,
        setLocations,
    } = params;

    const [searchParams] = useSearchParams();
    const initialLocationId = searchParams.get('location');

    const [hasFetchedLocations, setHasFetchedLocations] = useState(false);

    const locationId = appointmentFilters.selectedLocationIds?.[0] ?? '';
    const { loading } = useQuery(GET_REGION_LOCATIONS, {
        skip: hasFetchedLocations,
        variables: { id: locationId },
        onCompleted: (data) => {
            const { region } = data.locations[0];
            const regionLocations = region.locationSet;
            const locations = regionLocations.filter(
                (location: LocationPartsFragment) => location?.id !== VIRTUAL_CARE_LOCATION_ID
            );
            const selectedLocation = locations
                .find((location: LocationPartsFragment) => location.id === initialLocationId);

            if (!hasFetchedLocations) {
                setInitialRegionLocations(locations, selectedLocation, region.id);
            }
            setHasFetchedLocations(true);
        },
        onError: (error) => {
            setLocations([]);
            console.error(error.message);
            Sentry.captureException(error, {
                extra: {
                    locationId,
                    initialLocationId,
                    path: 'src/AppointmentSelection/hooks',
                    function: 'useLoadRegionLocationsOnMount'
                }
            });
        }
    });

    return { isLoadingRegionLocations: loading };
};

type WatchAvailableSlotsHookParams = {
    addDisabledDate: (date: string)=> void;
    appointmentFilters: FilterSelections;
    availableDates: string[];
    hasLoadedAvailability: boolean;
    resetDisabledDates: ()=> void;
    setAvailableSlots: (slots: Slot[] | undefined)=> void;
    setSelectedDateAppointmentFilter: (date: FilterSelections['selectedDate'])=> void;
};

export const useWatchAvailableSlots = (params: WatchAvailableSlotsHookParams) => {
    const {
        addDisabledDate,
        appointmentFilters,
        availableDates,
        hasLoadedAvailability,
        resetDisabledDates,
        setAvailableSlots,
        setSelectedDateAppointmentFilter,
    } = params;

    const [selectedLocationId, setSelectedLocationId] = useState<string>(appointmentFilters.selectedLocationIds?.[0] ?? '0');

    const [getAvailableSlots] = useLazyQuery(GET_AVAILABLE_SLOTS, {
        // Only get available slots if we've already loaded availability to prevent extraneous API calls
        // during state initialization. Otherwise, we could be making API calls to fetch slots for a day
        // with no availability.
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const { slots } = data;
            if (!slots.length) {
                const nextAvailableDate = (
                    availableDates.find((date) => DateTime.fromISO(date) > appointmentFilters.selectedDate)
                );
                const daysUntilEndOfMonth = appointmentFilters.selectedDate.endOf('month').day - appointmentFilters.selectedDate.day;

                const daysUntilNextAvailableDate = nextAvailableDate
                    ? Math.floor(DateTime.fromISO(nextAvailableDate).diff(appointmentFilters.selectedDate, 'days').days)
                    : daysUntilEndOfMonth;

                const nextDay = appointmentFilters.selectedDate.plus({ days: daysUntilNextAvailableDate });
                if (nextDay.month === appointmentFilters.selectedDate.month && daysUntilNextAvailableDate) {
                    setSelectedDateAppointmentFilter(nextDay);
                }
                addDisabledDate(appointmentFilters.selectedDate.toISODate());
            }

            const filterLocationId = appointmentFilters.selectedLocationIds?.[0] ?? '0';
            if (filterLocationId !== selectedLocationId) {
                resetDisabledDates();
                setSelectedLocationId(filterLocationId);
            }
            setAvailableSlots(slots);
        },
        onError: (error) => console.error(error.message)
    });

    useEffect(() => {
        // Only get available slots if we've already loaded availability to prevent extraneous API calls
        // during state initialization. Otherwise, we could be making API calls to fetch slots for a day
        // with no availability.
        if (hasLoadedAvailability) {
            // Get slots for first available date at selected location
            const date = appointmentFilters.selectedDate;
            const locationId = appointmentFilters.selectedLocationIds
                ? appointmentFilters.selectedLocationIds[0]
                : '0';
            const startOfDay = date.toISODate() === DateTime.now().toISODate()
                // Add 15 minute buffer to current time to prevent clients from booking within
                // 15 minutes of appointment start
                ? DateTime.now().plus({ minutes: 15 }).toUTC().toISO()
                : date.startOf('day').toUTC().toISO();
            const endOfDay = date.endOf('day').toUTC().toISO();

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getAvailableSlots({
                variables: { locationId, startGte: startOfDay, endLte: endOfDay }
            });
        }
    }, [
        appointmentFilters.selectedDate,
        appointmentFilters.selectedLocationIds,
        getAvailableSlots,
        hasLoadedAvailability,
    ]);
};
